import { useState, useCallback, useRef, useEffect } from 'react';
import type { QueryParam } from '../../../types/wizardTypes';

interface UseQueryMapBuilderOptions {
  watch: (field: string) => unknown;
  setValue: (field: string, value: unknown, options?: Record<string, boolean>) => void;
}

const normalizeTemplateToken = (s: string) =>
  s
    .replace(/%7B%7B/gi, '{{')
    .replace(/%7D%7D/gi, '}}')
    .trim();

const isTemplateVar = (s: string) =>
  /\{\{\s*[^{}]+\s*\}\}/.test(normalizeTemplateToken(s));

const encodeQueryComponentForPathInput = (s: string) =>
  encodeURIComponent(s).replace(/%20/g, ' ');

export const buildQueryMapFromPath = (path: string, queryMap: QueryParam[]) => {
  const [, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  const existingSelectedByKey = new Map<string, QueryParam[]>();
  const unselectedParams = queryMap.filter((item) => !item.selected);

  queryMap
    .filter((item) => item.selected)
    .forEach((item) => {
      const itemsForKey = existingSelectedByKey.get(item.key) || [];
      itemsForKey.push(item);
      existingSelectedByKey.set(item.key, itemsForKey);
    });

  const updatedQueryMap = Array.from(params.entries()).map(([key, value]) => {
    const existingItem = existingSelectedByKey.get(key)?.shift();
    return {
      key,
      value,
      dynamic: isTemplateVar(value),
      selected: existingItem?.selected ?? true,
    };
  });

  updatedQueryMap.push(...unselectedParams.map((param) => ({ ...param })));
  return updatedQueryMap;
};

export function useQueryMapBuilder({ watch, setValue }: UseQueryMapBuilderOptions) {
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const snapshotRef = useRef<string>('');
  const updatingFromPath = useRef(false);
  const updatingFromQuery = useRef(false);
  const isInitialMount = useRef(true);

  const queryMap: QueryParam[] = (watch('queryMap') as QueryParam[]) || [];
  const queryMapStr = JSON.stringify(queryMap, null, 2);
  const path = (watch('path') as string) || '';
  const queryMapRef = useRef<QueryParam[]>(queryMap);
  const pathRef = useRef(path);

  queryMapRef.current = queryMap;
  pathRef.current = path;

  // Keep snapshot in sync when not editing JSON
  if (snapshotRef.current !== queryMapStr && !showJson) {
    snapshotRef.current = queryMapStr;
  }

  // Initialize JSON text when opening the editor
  useEffect(() => {
    if (showJson) {
      setJsonText(JSON.stringify(queryMap, null, 2));
    }
  }, [showJson, queryMapStr]);

  const syncQueryMapFromPath = useCallback((nextPath: string) => {
    const nextQueryMap = buildQueryMapFromPath(nextPath, queryMapRef.current);
    const nextQueryMapStr = JSON.stringify(nextQueryMap, null, 2);
    if (nextQueryMapStr === JSON.stringify(queryMapRef.current, null, 2)) {
      // Even when queryMap is unchanged, block the queryMap→path effect from
      // stripping a trailing `?` that the user just typed.
      updatingFromPath.current = true;
      return;
    }
    updatingFromPath.current = true;
    setValue('queryMap', nextQueryMap, { shouldDirty: true });
  }, [setValue]);

  // Sync JSON text when queryMap changes externally while editor is open
  useEffect(() => {
    if (!showJson) return;
    const newStr = JSON.stringify(queryMap, null, 2);
    if (newStr !== snapshotRef.current) {
      snapshotRef.current = newStr;
      setJsonText(newStr);
    }
  }, [queryMapStr]);

  // Path → queryMap sync
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Skip on first mount if queryMap already has selected entries -
      // they were set externally (e.g. by AI) while this component was unmounted.
      // The QueryMap→path sync below will update the path to match.
      if (queryMapRef.current.some(q => q.selected)) return;
    }
    if (updatingFromQuery.current) {
      updatingFromQuery.current = false;
      return;
    }
    syncQueryMapFromPath(path);
  }, [path, syncQueryMapFromPath]);

  // QueryMap → path sync
  useEffect(() => {
    if (updatingFromPath.current) {
      updatingFromPath.current = false;
      return;
    }
    const basePath = path.split('?')[0];
    const selectedPairs = queryMap.filter(q => q.selected && q.key);
    const nextPath = selectedPairs.length === 0
      ? basePath
      : `${basePath}?${selectedPairs
        .map(q => {
          const rawKey = String(q.key || '');
          const rawValue = String(q.value || '');
          const normalizedKey = normalizeTemplateToken(rawKey);
          const normalizedValue = normalizeTemplateToken(rawValue);

          const key =
            rawKey.startsWith('$') || isTemplateVar(rawKey)
              ? normalizedKey
              : encodeQueryComponentForPathInput(rawKey);
          const value = isTemplateVar(rawValue)
            ? normalizedValue
            : encodeQueryComponentForPathInput(rawValue);
          return `${key}=${value}`;
        })
        .join('&')}`;

    if (nextPath === pathRef.current) {
      return;
    }

    updatingFromQuery.current = true;
    setValue('path', nextPath, { shouldDirty: true });
  }, [path, queryMap, setValue]);

  const addParam = useCallback(() => {
    setValue('queryMap', [...queryMap, { key: '', value: '', selected: true, dynamic: false }], { shouldDirty: true });
  }, [queryMap, setValue]);

  const updateParam = useCallback(
    (idx: number, field: keyof QueryParam, fieldValue: QueryParam[keyof QueryParam]) => {
      const updated = queryMap.map((q, i) =>
        i === idx ? { ...q, [field]: fieldValue } : q
      );
      setValue('queryMap', updated, { shouldDirty: true });
    },
    [queryMap, setValue]
  );

  const removeParam = useCallback(
    (idx: number) => {
      setValue('queryMap', queryMap.filter((_, i) => i !== idx), { shouldDirty: true });
    },
    [queryMap, setValue]
  );

  const toggleDynamic = useCallback(
    (idx: number) => {
      const updated = queryMap.map((q, i) => {
        if (i !== idx) return q;
        if (q.dynamic) {
          const v = (q.value || '').replace(/[{}]/g, '');
          return { ...q, dynamic: false, value: v };
        }
        let v = (q.value || '').trim();
        const hasOpen = v.includes('{{');
        const hasClose = v.includes('}}');
        if (hasOpen && !hasClose) {
          v = v + '}}';
        } else if (!hasOpen && hasClose) {
          v = '{{' + v;
        } else if (!hasOpen && !hasClose) {
          v = `{{${v}}}`;
        }
        return { ...q, dynamic: true, value: v };
      });
      setValue('queryMap', updated, { shouldDirty: true });
    },
    [queryMap, setValue]
  );

  const setFromJson = useCallback(
    (val: string) => {
      setJsonText(val);
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) {
          const parsedStr = JSON.stringify(parsed, null, 2);
          snapshotRef.current = parsedStr;
          if (parsedStr !== JSON.stringify(queryMapRef.current, null, 2)) {
            setValue('queryMap', parsed, { shouldDirty: true });
          }
        }
      } catch {
        // allow intermediate typing
      }
    },
    [setValue]
  );

  const setQueryMap = useCallback(
    (params: QueryParam[]) => {
      if (JSON.stringify(params, null, 2) !== JSON.stringify(queryMapRef.current, null, 2)) {
        setValue('queryMap', params, { shouldDirty: true });
      }
    },
    [setValue]
  );

  return {
    queryMap,
    showJson,
    setShowJson,
    jsonText,
    addParam,
    updateParam,
    removeParam,
    toggleDynamic,
    setFromJson,
    setQueryMap,
    syncQueryMapFromPath,
  };
}
